import { useEffect, useMemo, useState } from "react";

import { JobTitleScreeningPreview } from "@signalist/shared-types";

import { useScreenPreviewMutation } from "@/pages/Private/redux/csvEnrichment/csvEnrichment.api";

import { CsvEnrichmentType } from "@/enum/csv-enrichment.enum";

import {
	computeRowLevelStats,
	FoundFieldMapping,
	matchHeaderIndex,
	ParsedXlsxData,
	RowLevelStats,
} from "./titleGate.utils";

const PREVIEW_DEBOUNCE_MS = 400;
const MAX_TITLE_LENGTH = 255;

interface UseTitleScreeningPreviewArgs {
	entityType?: string | null;
	foundFields?: FoundFieldMapping[];
	parsedData?: ParsedXlsxData;
	isFeatureActive?: boolean;
}

interface UseTitleScreeningPreviewResult {
	gateVisible: boolean;
	statsVisible: boolean;
	preview?: JobTitleScreeningPreview;
	isPreviewLoading: boolean;
	rowStats: RowLevelStats;
}

/**
 * Drives the PREC-3536 title-gate on the XLSX upload form: decides gate visibility, computes the
 * client-side row statistics, and fires the debounced screen-preview dry-run (re-running when the
 * file or the mapped title column changes). Only calls the endpoint when the feature flag is on
 * and a title column is mapped — the endpoint 403s with the flag off.
 */
export const useTitleScreeningPreview = ({
	entityType,
	foundFields,
	parsedData,
	isFeatureActive,
}: UseTitleScreeningPreviewArgs): UseTitleScreeningPreviewResult => {
	const [screenPreview, { isLoading }] = useScreenPreviewMutation();
	const [preview, setPreview] = useState<JobTitleScreeningPreview>();

	const titleColumnIndex = useMemo(() => {
		const titleField = foundFields?.find((field) => field.assignedField === "title");

		if (!titleField || !parsedData) {
			return -1;
		}

		return matchHeaderIndex(parsedData.headers, titleField.foundField);
	}, [foundFields, parsedData]);

	const gateVisible = useMemo(() => {
		if (!isFeatureActive || !parsedData) {
			return false;
		}

		if (entityType === CsvEnrichmentType.PERSON_BY_TITLE) {
			return true;
		}

		return entityType === CsvEnrichmentType.PERSON && titleColumnIndex >= 0;
	}, [isFeatureActive, parsedData, entityType, titleColumnIndex]);

	/**
	 * PREC-3870: the statistics box is **not** the screening gate. Rows detected / ready /
	 * duplicates are a property of the file, not of job titles, so they show for a company upload
	 * and for a workspace without the screening feature — the two were bundled only because the
	 * box shipped alongside the toggle (PREC-3536 Phase 4), and that left company lists with no
	 * upload feedback at all.
	 */
	const statsVisible = !!parsedData;

	const rowStats = useMemo(() => computeRowLevelStats(parsedData), [parsedData]);

	useEffect(() => {
		if (!gateVisible || titleColumnIndex < 0 || !parsedData?.rows.length) {
			setPreview(undefined);

			return;
		}

		const timer = setTimeout(async () => {
			const titles = parsedData.rows.map((row) =>
				(row[titleColumnIndex] ?? "").trim().slice(0, MAX_TITLE_LENGTH)
			);

			try {
				setPreview(await screenPreview({ titles }).unwrap());
			} catch {
				// Preview is best-effort — the gate stays usable without the numbers.
				setPreview(undefined);
			}
		}, PREVIEW_DEBOUNCE_MS);

		return () => clearTimeout(timer);
	}, [gateVisible, titleColumnIndex, parsedData, screenPreview]);

	return { gateVisible, statsVisible, preview, isPreviewLoading: isLoading, rowStats };
};
